import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';

import api from '../../../../api/axios';
import { IApiResponseError, IApiState } from '../../../../types/api';
import { RootState } from '../../..';
import { getApiErrorMessage, getSavingsAuthConfig } from '../utils';

interface ICreateAutoDebitConfigRequest {
  debitAccountNumber: string;
  savingsAccountNumber: string;
  autoDebitFrequency: string;
  amount: number;
  dayOfWeek?: string;
  dayOfMonth?: number | string;
  startDate?: string;
  endDate?: string;
  pin?: string;
}

interface ICreateAutoDebitConfigResponse {
  status?: boolean;
  message?: string;
  data?: { id: string };
  errors?: IApiResponseError[];
}

const initialState: IApiState<ICreateAutoDebitConfigResponse> = {
  value: null,
  status: 'idle',
  error: '',
};

export const createAutoDebitConfigAsync = createAsyncThunk(
  'savings/autoDebit/create',
  async (
    values: ICreateAutoDebitConfigRequest,
    { rejectWithValue, getState },
  ) => {
    try {
      const state = getState() as RootState;

      const { data } = await api.post<ICreateAutoDebitConfigResponse>(
        '/savings/auto-debit-configurations',
        values,
        getSavingsAuthConfig(state),
      );

      return data;
    } catch (err) {
      const error = err as AxiosError<ICreateAutoDebitConfigResponse>;
      return rejectWithValue(error.response?.data);
    }
  },
);

export const createAutoDebitConfigSlice = createSlice({
  name: 'savings/autoDebit/create',
  initialState,
  reducers: {
    resetCreateAutoDebitConfig(state) {
      state.value = null;
      state.status = 'idle';
      state.error = '';
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(createAutoDebitConfigAsync.pending, (state) => {
        state.status = 'loading';
        state.error = '';
      })
      .addCase(createAutoDebitConfigAsync.fulfilled, (state, action) => {
        state.status = 'success';
        state.value = action.payload as ICreateAutoDebitConfigResponse;
      })
      .addCase(createAutoDebitConfigAsync.rejected, (state, action) => {
        state.status = 'failed';
        state.value = action.payload as ICreateAutoDebitConfigResponse;
        state.error = getApiErrorMessage(action.payload);
      });
  },
});

export const { resetCreateAutoDebitConfig } = createAutoDebitConfigSlice.actions;

export default createAutoDebitConfigSlice.reducer;
